import {
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  CircleDollarSign,
  MessageCircle,
  ShoppingBasket,
  Sparkles,
  Target,
} from "lucide-react";

import { Summary } from "../../../../components/ui/Summary";
import { CrmPetDiagnosisController } from "../../../../services/controllers/crmPetDiagnosis";
import { useCrmPetDiagnosis } from "../../../../store/crmPetDiagnosis/useCrmPetDiagnosis";
import { CRM_PET_RULES } from "../constants";
import { formatCurrency, formatNumber } from "../helpers";
import { MetricCard } from "../components/MetricCard";
import { SectionCard } from "../components/SectionCard";
import { SectionError } from "../components/SectionError";

const OPPORTUNITY_METRICS = [
  { ruleCode: CRM_PET_RULES.ABSENT, icon: Target, label: "Ausentes" },
  { ruleCode: CRM_PET_RULES.POTENTIAL_SUBSCRIBER, icon: BadgeCheck, label: "Possíveis assinantes" },
  { ruleCode: CRM_PET_RULES.NO_ADDITIONAL, icon: Sparkles, label: "Sem adicionais" },
  { ruleCode: CRM_PET_RULES.NO_PRODUCT, icon: ShoppingBasket, label: "Não compram produtos" },
];

export function DiagnosisSummary() {
  const controller = CrmPetDiagnosisController();
  const opportunities = useCrmPetDiagnosis((state) => state.data.opportunities);
  const kpis = useCrmPetDiagnosis((state) => state.data.overviewKpis);
  const loading = useCrmPetDiagnosis((state) => state.data.loading.diagnosisSummary);
  const error = useCrmPetDiagnosis((state) => state.data.errors.diagnosisSummary);
  const filters = useCrmPetDiagnosis((state) => state.data.filters.overview);

  const resultsSearch = `tab=results&resultsPeriod=${filters.period}&resultsFrom=${filters.from}&resultsTo=${filters.to}`;
  const operationMetrics = OPPORTUNITY_METRICS.map((metric) => ({
    id: `crm-diagnosis-opportunity-${metric.ruleCode}`,
    icon: metric.icon,
    value: formatNumber(opportunities?.byRule?.[metric.ruleCode]),
    label: metric.label,
    loading,
    link: `/vendas/crm-pet?rule=${metric.ruleCode}&source=diagnosis`,
  }));
  const resultMetrics = [
    {
      id: "crm-diagnosis-contacted-customers",
      icon: MessageCircle,
      value: formatNumber(kpis?.contactedAttempts),
      label: "Clientes contatados",
      loading,
    },
    {
      id: "crm-diagnosis-scheduled",
      icon: CalendarCheck,
      value: formatNumber(kpis?.scheduledCustomers),
      label: "Tiveram agendamento",
      loading,
    },
    {
      id: "crm-diagnosis-finished",
      icon: CheckCircle2,
      value: formatNumber(kpis?.finishedCustomers),
      label: "Agendamentos finalizados",
      loading,
      link: `/diagnosis/crm-pet?${resultsSearch}&resultsStatus=FINISHED`,
    },
    {
      id: "crm-diagnosis-revenue",
      icon: CircleDollarSign,
      value: formatCurrency(kpis?.totalRevenue),
      label: "Receita atribuída ao CRM Pet",
      loading,
      link: `/diagnosis/crm-pet?${resultsSearch}&resultsContacted=true`,
    },
  ];

  return (
    <div className="grid items-start gap-5 xl:grid-cols-2">
      <SectionCard
        title="Oportunidades atuais por régua"
        description="Pets elegíveis agora. Contagem independente do período selecionado."
        icon={Target}
      >
        {error ? (
          <SectionError message={error} onRetry={() => controller.retrySection("diagnosisSummary")} />
        ) : (
          <Summary.Root>
            {operationMetrics.map((metric) => <MetricCard key={metric.id} {...metric} />)}
          </Summary.Root>
        )}
      </SectionCard>

      <SectionCard
        title="Resultado acumulado"
        description="Tentativas únicas no período, sem duplicar clientes ligados a várias réguas."
        icon={CalendarCheck}
      >
        {error ? (
          <SectionError message={error} onRetry={() => controller.retrySection("diagnosisSummary")} />
        ) : (
          <Summary.Root>
            {resultMetrics.map((metric) => <MetricCard key={metric.id} {...metric} />)}
          </Summary.Root>
        )}
      </SectionCard>
    </div>
  );
}
